import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PokerCard } from "@/features/poker/components/PokerCard";
import { useWindowSize } from "@/features/poker/hooks/useWindowSize";
import { Flame, Coins } from "lucide-react";
import { cn } from "@/lib/utils";
import Logo from "@/assets/images/logo.png";
import type { RoundData, PreflopAction, PriorAction } from "./types";

const ALL_POSITIONS = ["UTG", "UTG1", "MP", "MP1", "CO", "BTN", "SB", "BB"];

const POSITION_BORDER: Record<string, string> = {
  UTG: "border-red-500/50",
  UTG1: "border-red-400/40",
  MP: "border-orange-500/50",
  MP1: "border-orange-400/40",
  CO: "border-yellow-500/50",
  BTN: "border-green-500/50",
  SB: "border-blue-500/50",
  BB: "border-purple-500/50",
};

const POSITION_TEXT: Record<string, string> = {
  UTG: "text-red-400",
  UTG1: "text-red-300",
  MP: "text-orange-400",
  MP1: "text-orange-300",
  CO: "text-yellow-400",
  BTN: "text-green-400",
  SB: "text-blue-400",
  BB: "text-purple-400",
};

interface Props {
  round: RoundData;
  roundNumber: number;
  totalRounds: number;
  score: number;
  streak: number;
  onAnswer: (action: PreflopAction) => void;
  disabled: boolean;
}

export const PreflopRound = ({
  round,
  roundNumber,
  totalRounds,
  score,
  streak,
  onAnswer,
  disabled,
}: Props) => {
  const { t } = useTranslation();
  const { width = 0, height = 0 } = useWindowSize();
  const [timeLeft, setTimeLeft] = useState(round.timeLimit);
  const startRef = useRef(Date.now());

  useEffect(() => {
    startRef.current = Date.now();
    setTimeLeft(round.timeLimit);

    const interval = setInterval(() => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      const remaining = Math.max(0, round.timeLimit - elapsed);
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        if (!disabled) onAnswer("fold");
      }
    }, 100);

    return () => clearInterval(interval);
    // This effect intentionally re-runs only on [round, roundNumber]: it (re)starts
    // the per-round countdown and must NOT restart when `disabled` toggles (it flips
    // true right as the player answers, mid-round) or when `onAnswer` is re-created
    // by the parent on every render (not memoized) — either would reset the timer
    // far more often than once per round. `disabled`/`onAnswer` are read from the
    // latest render's closure at the moment the timer expires, which is correct here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round, roundNumber]);

  const timerPercent = (timeLeft / round.timeLimit) * 100;
  const timerColor =
    timeLeft <= 2 ? "bg-destructive" : timeLeft <= 4 ? "bg-yellow-500" : "bg-primary";

  const priorActions = round.priorActions ?? [];
  const priorActionMap = new Map<string, PriorAction["action"]>(
    priorActions.map((pa) => [pa.position, pa.action])
  );

  // Table layout
  const containerWidth = Math.min(Math.max(width ? width - 32 : 360, 300), 600);
  const isMobile = width < 768;
  const tableHeight = isMobile && height ? Math.min(height * 0.42, 280) : 280;
  const seatW = isMobile ? 68 : 80;
  const seatH = isMobile ? 56 : 64;

  const numPositions = ALL_POSITIONS.length;
  const playerPosIndex = ALL_POSITIONS.indexOf(round.position);
  const angleOffset = Math.PI - (playerPosIndex / numPositions) * 2 * Math.PI;

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="preflop-round">
      <div className="p-3 space-y-3 max-w-screen-xl mx-auto">
        {/* Top bar */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground" data-testid="round-indicator">
              {t('preflop_round', { current: roundNumber, total: totalRounds })}
            </span>
            <div className="flex items-center gap-3">
              {streak >= 3 && (
                <div className="flex items-center gap-1 text-orange-400">
                  <Flame className="h-4 w-4" />
                  <span className="font-bold text-xs">{streak}x</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <Coins className="h-4 w-4 text-secondary" />
                <span className="font-bold text-secondary">{score}</span>
              </div>
            </div>
          </div>
          <Progress value={(roundNumber / totalRounds) * 100} className="h-1.5" />
        </div>

        {/* Timer */}
        <div className="w-full h-1.5 bg-secondary/20 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-100 ${timerColor}`}
            style={{ width: `${timerPercent}%` }}
          />
        </div>

        {/* Poker Table */}
        <div className="flex w-full justify-center">
          <div className="relative" style={{ width: containerWidth, height: tableHeight }}>
            {/* Felt */}
            <div className="absolute inset-0 rounded-[48%] bg-gradient-to-b from-primary/70 to-primary shadow-2xl border-[16px] border-black" />
            <div className="absolute inset-5 rounded-[45%] bg-black/30 blur-2xl" />

            {/* Center */}
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-white z-10">
              <img src={Logo} alt="" className="absolute inset-0 m-auto h-32 w-32 opacity-10 pointer-events-none" />
              <div className="relative z-10 rounded-full border border-white/20 bg-black/30 px-3 py-1 text-center text-xs backdrop-blur">
                <span className="text-white/60">{t('preflop_blinds')} </span>
                <span className="font-semibold text-secondary">{round.blinds.sb}/{round.blinds.bb}</span>
              </div>
            </div>

            {/* Seats */}
            {ALL_POSITIONS.map((pos, index) => {
              const angle = (index / numPositions) * 2 * Math.PI - Math.PI / 2 + angleOffset;
              const radiusX = containerWidth / 2 - seatW / 2;
              const radiusY = tableHeight / 2 - seatH / 2;
              const x = containerWidth / 2 + radiusX * Math.cos(angle);
              const y = tableHeight / 2 + radiusY * Math.sin(angle);

              const isPlayer = pos === round.position;
              const priorAction = priorActionMap.get(pos);
              const isFolded = priorAction === "fold";

              return (
                <div
                  key={pos}
                  data-testid={`seat-${pos}`}
                  className={cn(
                    "absolute flex flex-col items-center justify-center rounded-lg border px-1 py-1 text-xs text-white backdrop-blur transition-all",
                    "bg-black/60 shadow-[0_8px_20px_rgba(0,0,0,0.35)]",
                    isPlayer
                      ? "border-amber-400 ring-1 ring-amber-400/30 z-20"
                      : POSITION_BORDER[pos] || "border-white/20",
                    isFolded && !isPlayer && "opacity-40"
                  )}
                  style={{
                    left: x,
                    top: y,
                    width: seatW,
                    minHeight: seatH,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  {isPlayer ? (
                    <>
                      <div className="flex items-center gap-0.5">
                        {round.hand.map((card, i) => (
                          <PokerCard key={i} card={card} compact />
                        ))}
                      </div>
                      <span className="text-[9px] font-bold text-amber-400 mt-0.5">{pos}</span>
                    </>
                  ) : (
                    <>
                      <span className={cn("font-mono font-bold text-[10px]", POSITION_TEXT[pos] || "text-white/70")}>
                        {pos}
                      </span>
                      {priorAction !== undefined ? (
                        <span
                          className={cn(
                            "text-[9px] mt-0.5 font-semibold uppercase",
                            priorAction === "raise"
                              ? "text-amber-400"
                              : priorAction === "call"
                                ? "text-secondary"
                                : "text-muted-foreground"
                          )}
                        >
                          {priorAction === "fold" ? t('poker_fold') : priorAction === "call" ? t('poker_call') : t('poker_raise')}
                        </span>
                      ) : (
                        <PokerCard hidden compact />
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Game info bar — pot + stack like a real game */}
        {(() => {
          const bb = round.blinds.bb;
          const hasRaise = priorActions.some((pa) => pa.action === "raise");
          const hasLimp = priorActions.some((pa) => pa.action === "call");
          // Calculate pot based on prior actions
          const foldCount = priorActions.filter((pa) => pa.action === "fold").length;
          const callCount = priorActions.filter((pa) => pa.action === "call").length;
          const raiseCount = priorActions.filter((pa) => pa.action === "raise").length;
          const pot = round.blinds.sb + bb + callCount * (hasRaise ? bb * 3 : bb) + raiseCount * bb * 3;
          const toCall = hasRaise ? bb * 3 : (round.position === "BB" && !hasRaise ? 0 : bb);
          const minRaise = hasRaise ? bb * 3 * 2 : bb * 3;

          return (
            <>
              <div className="flex items-center justify-between text-sm px-1">
                <div className="flex items-center gap-1.5">
                  <Coins className="h-4 w-4 text-secondary" />
                  <span className="text-muted-foreground">{t('preflop_pot')}</span>
                  <span className="font-bold text-secondary">{pot}</span>
                </div>
                <div className="text-muted-foreground">
                  {t('preflop_stack')} <span className="font-bold text-white">{round.stack.toLocaleString()}</span>
                </div>
              </div>

              {/* Action buttons — styled like a real game */}
              <div className="grid grid-cols-3 gap-3 pt-1">
                <Button
                  variant="destructive"
                  size="lg"
                  className="flex flex-col items-center py-5 gap-0.5"
                  onClick={() => onAnswer("fold")}
                  disabled={disabled}
                >
                  <span className="text-lg font-bold">{t('poker_fold')}</span>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="flex flex-col items-center py-5 gap-0.5 border-secondary/50 text-secondary hover:bg-secondary/10"
                  onClick={() => onAnswer("call")}
                  disabled={disabled}
                >
                  <span className="text-lg font-bold">
                    {round.position === "BB" && !hasRaise ? t('poker_check') : t('poker_call')}
                  </span>
                  {(round.position !== "BB" || hasRaise) && (
                    <span className="text-[11px] opacity-70">{toCall} {t('poker_chips')}</span>
                  )}
                </Button>
                <Button
                  size="lg"
                  className="flex flex-col items-center py-5 gap-0.5 bg-secondary text-secondary-foreground hover:bg-secondary/90"
                  onClick={() => onAnswer("raise")}
                  disabled={disabled}
                >
                  <span className="text-lg font-bold">
                    {hasRaise ? t('poker_3bet') : t('poker_raise')}
                  </span>
                  <span className="text-[11px] opacity-70">{minRaise} {t('poker_chips')}</span>
                </Button>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
};
